import React, {
    FocusEventHandler,
    ReactNode,
    forwardRef,
    KeyboardEventHandler,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import styled from 'styled-components';
import composeRefs from '@seznam/compose-react-refs';
import { hideInPrint } from '../../styles/mixins';
import MockPrintInput from './MockPrintInput';
import TagsInputRow, { TagsInputRowRef } from '../HBTextField/TagsInputRow';
import {
    Input,
    FieldContainer,
    ContentArea,
    AffixSlot,
    DSInnerInput,
    ClearIconButton
} from './HBInput.styles';
import { HBInputProps } from './HBInput.types';
import type { TTextFieldSize } from '../HBTextField/variables';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfTestIdProps } from '../common/internal/test-id.types';
import { XCircleFilled16 } from '@honeybook/icons';

// Returns true when the affix node is text-based (plain string or a DOM element
// like <span>) rather than a React component (e.g. an SVG icon). Used to apply
// the 2px optical baseline correction only to text affixes.
const isTextAffix = (node: ReactNode): boolean => {
    if (typeof node === 'string' || typeof node === 'number') {
        return true;
    }
    if (React.isValidElement(node)) {
        return typeof node.type === 'string';
    }
    return false;
};

/**
 * DS mode (`isNewHBMode`) is designed for the `sm`/`md`/`lg` sizes. Clamp the
 * legacy `xs`/`xl` sizes to the nearest DS size (`xs` → `sm`, `xl` → `lg`) so a
 * wrong size still renders correctly.
 */
export const clampDSSize = (size: TTextFieldSize): TTextFieldSize =>
    size === 'xs' ? 'sm' : size === 'xl' ? 'lg' : size;

const HBInput = forwardRef<
    HTMLInputElement,
    HBInputProps & MustIncludeOneOfTestIdProps
>((props, ref) => {
    const {
        size = 'md',
        customTheme,
        width,
        scope = 'system',
        onBlur,
        onKeyDown,
        onEnterKey,
        blurOnEnterKey,
        onFocus,
        selectOnFocus,
        testIdName,
        testId,
        required,
        $showBorderWhenRequired = false,
        inputStyle,
        prefix,
        suffix,
        clearable,
        onClear,
        tags,
        onTagRemove,
        // Destructure non-HTML props so they don't reach the native <input>
        error,
        isNewHBMode,
        ...baseProps
    } = props;

    const { name, id } = baseProps;

    const innerRef = useRef<HTMLInputElement>(null);
    const suffixRef = useRef<HTMLDivElement>(null);
    const lastOnBlur = useRef<EventListener | null>(null);
    const tagsRowRef = useRef<TagsInputRowRef>(null);

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id || name,
        suffix: 'input',
        componentName: 'HBInput'
    });

    const [hasValue, setHasValue] = useState(
        !!props.defaultValue || !!props.value
    );

    const [isFocused, setIsFocused] = useState(false);

    const hasTags = (tags?.length ?? 0) > 0;

    const handleFocus: FocusEventHandler<HTMLInputElement> = ev => {
        setIsFocused(true);
        if (selectOnFocus) {
            ev.currentTarget.select();
        }
        if (typeof onFocus === 'function') {
            onFocus(ev);
        }
    };

    const handleBlur: FocusEventHandler<HTMLInputElement> = ev => {
        setIsFocused(false);
        onBlur?.(ev);
        setHasValue(!!innerRef.current?.value);
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = ev => {
        if (ev.keyCode === 13) {
            if (blurOnEnterKey) {
                ev.currentTarget.blur();
            }
            if (typeof onEnterKey === 'function') {
                onEnterKey(ev);
            }
        }
        if (typeof onKeyDown === 'function') {
            onKeyDown(ev);
        }
    };

    const handleKeyDownDS: KeyboardEventHandler<HTMLInputElement> = ev => {
        // Backspace on empty input removes last tag
        if (
            ev.key === 'Backspace' &&
            !ev.currentTarget.value &&
            tags &&
            tags.length > 0 &&
            typeof onTagRemove === 'function'
        ) {
            onTagRemove(tags[tags.length - 1].id);
        }
        // ArrowLeft at cursor position 0 moves focus to the last tag's delete button
        if (
            ev.key === 'ArrowLeft' &&
            ev.currentTarget.selectionStart === 0 &&
            hasTags
        ) {
            ev.preventDefault();
            tagsRowRef.current?.focusLast();
        }
        handleKeyDown(ev);
    };

    const handleContainerClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (!suffixRef.current?.contains(e.target as Node)) {
                innerRef.current?.focus();
            }
        },
        []
    );

    const handleClearClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (innerRef.current) {
                // Use the native setter so React's _valueTracker sees
                // the change; the subsequent 'input' event bubbles up
                // to React's delegation root and fires onChange.
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                    window.HTMLInputElement.prototype,
                    'value'
                )?.set;
                nativeInputValueSetter?.call(innerRef.current, '');
                innerRef.current.dispatchEvent(
                    new Event('input', { bubbles: true })
                );
                innerRef.current.focus();
            }
            setHasValue(false);
            onClear?.();
        },
        [onClear]
    );

    useEffect(() => {
        if (!isNewHBMode && innerRef.current != null) {
            if (lastOnBlur.current != null) {
                innerRef.current.removeEventListener(
                    'blur',
                    lastOnBlur.current
                );
            }
            const newOnBlur: EventListener = ev => {
                onBlur?.(ev as unknown as React.FocusEvent<HTMLInputElement>);
                setHasValue(!!innerRef.current?.value);
            };
            innerRef.current.addEventListener('blur', newOnBlur);
            lastOnBlur.current = newOnBlur;
        }
    }, [innerRef, onBlur, isNewHBMode]);

    if (isNewHBMode) {
        const dsSize = clampDSSize(size);
        const showClear =
            clearable &&
            (hasValue || !!props.value || !!props.defaultValue || hasTags);

        return (
            <FieldContainer
                $size={dsSize}
                $variant={inputStyle ?? 'outlined'}
                $hasTags={hasTags}
                $error={!!error}
                $disabled={!!baseProps.disabled}
                $focused={isFocused}
                $customTheme={customTheme}
                onClick={handleContainerClick}
                data-testid={`${finalTestId}--container`}
            >
                {prefix && (
                    <AffixSlot
                        $isText={isTextAffix(prefix)}
                        data-testid={`${finalTestId}--prefix`}
                    >
                        {prefix}
                    </AffixSlot>
                )}

                <ContentArea $hasTags={hasTags}>
                    {hasTags && tags && (
                        <TagsInputRow
                            ref={tagsRowRef}
                            tags={tags}
                            onTagRemove={onTagRemove}
                            onNavigateToInput={() => innerRef.current?.focus()}
                            testIdPrefix={finalTestId}
                        />
                    )}

                    <DSInnerInput
                        autoComplete="off"
                        {...baseProps}
                        ref={composeRefs(ref, innerRef)}
                        $size={dsSize}
                        $hasTags={hasTags}
                        $customTheme={customTheme}
                        data-testid={finalTestId}
                        aria-invalid={error ? true : undefined}
                        onKeyDown={handleKeyDownDS}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                </ContentArea>

                {showClear && (
                    <ClearIconButton
                        testId={`${finalTestId}--clear`}
                        icon={XCircleFilled16}
                        label="Clear input"
                        size="xs"
                        buttonTheme="neutral-invisible"
                        type="button"
                        onClick={handleClearClick}
                    />
                )}

                {suffix && (
                    <AffixSlot
                        ref={suffixRef}
                        $isText={isTextAffix(suffix)}
                        data-testid={`${finalTestId}--suffix`}
                    >
                        {suffix}
                    </AffixSlot>
                )}
            </FieldContainer>
        );
    }

    return (
        <>
            <StyledInput
                {...baseProps}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                data-testid={finalTestId}
                ref={composeRefs(ref, innerRef)}
                size={size}
                customTheme={customTheme}
                scope={scope}
                width={width}
                error={error}
                $showBorderWhenRequired={
                    !!required && !!$showBorderWhenRequired && !hasValue
                }
            />
            {/* this Mock component mimicks the input styles in pdf print - where
            for some reason the inputs styles don't apply (i.e. original input
            is hidden, and the Mock is displayed) */}
            <MockPrintInput
                {...baseProps}
                size={size}
                customTheme={customTheme}
                scope={scope}
                width={width}
                error={error}
            />
        </>
    );
});

export default HBInput;

// -------------------------------- Styles -------------------------------- //
const StyledInput = styled(Input)`
    && {
        // && - to beat class specifity score.
        ${hideInPrint}
    }
`;
